import React, { useRef } from "react";
import { nextTabId } from "./tabHelpers";

// Onglets accessibles (pattern WAI-ARIA « tabs », activation automatique).
// tabs : [{ id, label, icon, badge, hint, dataPriv, hidden }]
// Les onglets masqués par les données ne sont pas rendus ; `dataPriv` reste
// posé sur le bouton pour que RoleStyle applique le masquage par rôle.
export const tabId = (prefix, id) => `${prefix}-tab-${id}`;
export const panelId = (prefix, id) => `${prefix}-panel-${id}`;

const Tabs = ({ tabs = [], activeId, onChange, idPrefix = "fiche", ariaLabel = "Sections" }) => {
  const visible = tabs.filter((t) => !t.hidden);
  const refs = useRef({});

  const focusAndSelect = (id) => {
    if (!id) return;
    onChange(id);
    const el = refs.current[id];
    if (el && el.focus) el.focus();
  };

  const onKeyDown = (e) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1 };
    if (keys[e.key]) {
      e.preventDefault();
      focusAndSelect(nextTabId(activeId, visible, keys[e.key]));
    } else if (e.key === "Home") {
      e.preventDefault();
      focusAndSelect(visible[0] && visible[0].id);
    } else if (e.key === "End") {
      e.preventDefault();
      focusAndSelect(visible[visible.length - 1] && visible[visible.length - 1].id);
    }
  };

  return (
    <div className="fiche-tabs" role="tablist" aria-label={ariaLabel} onKeyDown={onKeyDown}>
      {visible.map((tab) => {
        const selected = tab.id === activeId;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[tab.id] = el;
            }}
            type="button"
            role="tab"
            id={tabId(idPrefix, tab.id)}
            className="fiche-tabs__tab"
            aria-selected={selected}
            aria-controls={panelId(idPrefix, tab.id)}
            tabIndex={selected ? 0 : -1}
            data-priv={tab.dataPriv || undefined}
            onClick={() => onChange(tab.id)}
          >
            {tab.icon && <i className={`fa ${tab.icon}`} aria-hidden="true" />}
            {tab.label}
            {tab.badge !== undefined && tab.badge !== null && (
              <span className="fiche-tabs__badge">{tab.badge}</span>
            )}
            {tab.hint && <span className="fiche-tabs__hint">{tab.hint}</span>}
          </button>
        );
      })}
    </div>
  );
};

export const TabPanel = ({ id, activeId, idPrefix = "fiche", className = "", children }) => {
  const active = id === activeId;
  return (
    <div
      role="tabpanel"
      id={panelId(idPrefix, id)}
      aria-labelledby={tabId(idPrefix, id)}
      hidden={!active}
      className={`fiche-panel ${className}`.trim()}
      tabIndex={0}
    >
      {active && children}
    </div>
  );
};

export default Tabs;
